import { ArrowRight, FileText, Mic } from 'lucide-react';
import { Button } from '@/components/ui-v2/Button';
import { useFullPagerStore } from '@/modules/shared/store/useFullPagerStore';
import type { TaskItem } from '@/modules/shared/types/yc-demo';

const KIND_META: Record<TaskItem['kind'], { label: string; Icon: typeof FileText }> = {
  cv_review: { label: 'CV review', Icon: FileText },
  ai_interview_review: { label: 'AI interview review', Icon: Mic },
};

function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

function relativeDate(iso?: string) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  const day = 24 * 60 * 60 * 1000;
  const days = Math.round(ms / day);
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days === -1) return 'Overdue 1d';
  if (days < 0) return `Overdue ${Math.abs(days)}d`;
  if (days < 7) return `Due in ${days}d`;
  if (days < 30) return `Due in ${Math.round(days / 7)}w`;
  return `Due ${new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

export interface TaskCardProps {
  task: TaskItem;
  preview?: string | null;
}

export function TaskCard({ task, preview }: TaskCardProps) {
  const openFullPager = useFullPagerStore((s) => s.open);
  const { label: kindLabel, Icon: KindIcon } = KIND_META[task.kind];
  const due = relativeDate(task.dueAt);
  const overdue = task.dueAt ? new Date(task.dueAt).getTime() < Date.now() : false;
  const isHigh = task.priority === 'high';
  const isCompleted = task.status === 'completed';

  const priorityStyle = isHigh
    ? { background: 'var(--accent-muted)', color: 'var(--accent-strong)' }
    : { background: 'var(--surface-tertiary)', color: 'var(--text-secondary)' };

  function open() {
    // Open the unified FullPager overlay scoped to this task. The reviewer
    // never leaves /tasks — the overlay opens on top.
    openFullPager({
      entityType: 'application',
      entityId: task.applicationId,
      section: task.kind === 'cv_review' ? 'cv_screening' : 'ai_interview',
      taskId: task._id,
      scope:
        task.scope?.reportKinds && task.scope.reportKinds.length > 0
          ? { reportKinds: task.scope.reportKinds }
          : undefined,
      breadcrumbs: [
        { label: 'Tasks', href: '/tasks' },
        { label: task.candidateName },
      ],
    });
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          open();
        }
      }}
      className="group flex items-start gap-4 rounded-xl px-4 py-4 transition-colors duration-150 cursor-pointer outline-none"
      style={{ background: 'transparent' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-primary)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Avatar */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-full font-medium text-[13px] overflow-hidden"
        style={{
          width: 40,
          height: 40,
          background: 'var(--surface-tertiary)',
          color: 'var(--text-secondary)',
        }}
      >
        {task.candidateAvatarUrl ? (
          <img
            src={task.candidateAvatarUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <span>{initials(task.candidateName) || '?'}</span>
        )}
      </div>

      {/* Center */}
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-medium text-[14px]" style={{ color: 'var(--text-primary)' }}>
            {task.candidateName || 'Unnamed candidate'}
          </span>
          <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            · {task.jobTitle || 'Untitled role'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-[12px]">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
            style={{ background: 'var(--surface-tertiary)', color: 'var(--text-secondary)' }}
          >
            <KindIcon size={11} strokeWidth={1.8} />
            {kindLabel}
          </span>
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 capitalize"
            style={priorityStyle}
          >
            {task.priority}
          </span>
          {due ? (
            <span
              style={{
                color: overdue && !isCompleted ? 'var(--fg-danger)' : 'var(--text-tertiary)',
              }}
            >
              {due}
            </span>
          ) : null}
          {isCompleted && task.completedAt ? (
            <span style={{ color: 'var(--fg-success)' }}>
              Completed {new Date(task.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          ) : null}
        </div>

        {preview ? (
          <p
            className="text-[13px] leading-snug mt-0.5 line-clamp-2"
            style={{ color: 'var(--text-secondary)' }}
          >
            {preview}
          </p>
        ) : null}
      </div>

      {/* Right action */}
      <div className="flex-shrink-0 self-center">
        <Button
          variant="primary"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          Open report
          <ArrowRight size={13} strokeWidth={2} />
        </Button>
      </div>
    </div>
  );
}
